import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, CheckCircle, CreditCard, Trophy, AlertCircle, Sparkles } from 'lucide-react';
import api from '../services/api';

function Certificate() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Certificate price (shown only at this stage)
  const CERTIFICATE_PRICE = 499; // ₹499 for certificate

  useEffect(() => {
    const fetchEnrollment = async () => {
      try {
        const response = await api.get(`/enrollments/${enrollmentId}`);
        setEnrollment(response.data);

        // Check if eligible for certificate
        if (response.data.enrollmentStatus !== 'completed') {
          setError('Complete all sections to get certificate');
        }
      } catch (error) {
        console.error('Error fetching enrollment:', error);
        setError('Failed to load enrollment details');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollment();
  }, [enrollmentId]);

  const handleGetCertificate = async () => {
    setProcessing(true);
    setError('');

    try {
      // Simulate certificate payment
      const fakePaymentId = `CERT_PAY_${Date.now()}`;
      console.log('🎓 Simulating Certificate Payment:', fakePaymentId);

      // Issue certificate on backend
      await api.post(`/enrollments/${enrollmentId}/certificate`, {
        paymentId: fakePaymentId
      });

      alert('✅ Certificate issued successfully! Flow completed!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Certificate issuance failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading certificate details...</p>
        </div>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700">Enrollment not found</p>
          </div>
        </div>
      </div>
    );
  }

  if (enrollment.certificateIssued) {
    return (
      <div style={{marginTop: "73px"}} className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-12 text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <Trophy className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            🎓 Certificate Issued!
          </h1>
          <h2 className="text-2xl font-semibold text-slate-700 mb-6">
            {enrollment.program?.title}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Congratulations! Your experience certificate has been issued successfully.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{marginTop: "73px"}} className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full mb-4 shadow-xl">
            <Award className="w-12 h-12 text-slate-900" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Experience Certificate
          </h1>
          <p className="text-slate-300 text-lg">
            Complete your journey with an official certificate
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Success Banner */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-lg mb-1">Congratulations!</p>
                <p className="text-green-100">
                  You've completed all sections with {enrollment.overallProgress}% progress.
                </p>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Fake Payment Notice */}
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-yellow-900 mb-1">⚠️ DEVELOPMENT MODE - FAKE PAYMENT</h3>
                  <p className="text-yellow-800 text-sm mb-1">
                    This is a practice project. No real payment will be processed.
                  </p>
                  <p className="text-yellow-700 text-xs">
                    In production, this would integrate with Razorpay/Stripe
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* Program Details */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                {enrollment.program?.title}
              </h3>
              <div className="space-y-3 text-slate-700">
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="font-medium">Overall Progress:</span>
                  <span className="font-bold text-slate-900">{enrollment.overallProgress}%</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-200">
                  <span className="font-medium">Sections Completed:</span>
                  <span className="font-bold text-slate-900">
                    {enrollment.sectionsProgress?.filter(s => s.status === 'completed').length} / {enrollment.sectionsProgress?.length}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="font-medium">Status:</span>
                  <span className="flex items-center gap-2 text-green-700 font-bold">
                    <CheckCircle className="w-5 h-5" />
                    All Sections Completed
                  </span>
                </div>
              </div>
            </div>

            {/* Pricing */}
            {enrollment.enrollmentStatus === 'completed' && (
              <>
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-6 mb-6">
                  <div className="text-center">
                    <p className="text-slate-300 text-sm mb-2">Certificate Fee</p>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="text-5xl font-bold">₹{CERTIFICATE_PRICE}</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-slate-300">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">One-time payment</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-slate-300 mt-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Official experience certificate</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-slate-300 mt-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Downloadable & shareable</span>
                    </div>
                  </div>
                </div>

                <p className="text-center text-slate-600 mb-6">
                  Click below to process payment and receive your experience certificate
                </p>

                <button
                  onClick={handleGetCertificate}
                  disabled={processing}
                  className="w-full py-4 px-6 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-bold text-lg hover:from-green-700 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      Processing Payment...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-6 h-6" />
                      Pay ₹{CERTIFICATE_PRICE} & Get Certificate (Fake Payment)
                    </>
                  )}
                </button>
              </>
            )}

            {enrollment.enrollmentStatus !== 'completed' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
                <p className="text-yellow-800 font-medium">
                  Complete all sections to unlock certificate
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Certificate;
